import { COMPANY_LOGO, COMPANY_NAME } from '../config/brandingConfig';
import { PRINT_BASE_STYLES } from '../config/printConfig';

export const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

/** Screen-only bars with Print; hidden when printing or saving as PDF from the browser. */
const PRINT_PREVIEW_CHROME_STYLES = `
  .report-print-bar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .report-print-bar--bottom {
    border-bottom: none;
    border-top: 1px solid #e2e8f0;
    margin-top: 24px;
  }
  .report-print-bar button {
    font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
    font-size: 14px;
    font-weight: 600;
    padding: 10px 20px;
    border-radius: 8px;
    border: none;
    background: #0f172a;
    color: #fff;
    cursor: pointer;
  }
  .report-print-bar button:hover {
    background: #1e293b;
  }
  @media print {
    .report-print-bar {
      display: none !important;
    }
  }
`;

const buildReportHtml = ({
  title,
  subtitle = '',
  bodyHtml = '',
  extraStyles = '',
  includePrintControls = false,
}) => {
  const resolvedLogo = COMPANY_LOGO?.startsWith('/')
    ? `${window.location.origin}${COMPANY_LOGO}`
    : COMPANY_LOGO;
  const inner = `
        <div class="brand">
          <img src="${escapeHtml(resolvedLogo)}" alt="Company Logo" />
          <p class="brand-title">${escapeHtml(COMPANY_NAME)}</p>
        </div>
        <h1>${escapeHtml(title)}</h1>
        ${subtitle ? `<p class="subtitle">${escapeHtml(subtitle)}</p>` : ''}
        ${bodyHtml}
  `;
  const bodyContent = includePrintControls
    ? `
        <div class="report-print-bar">
          <button type="button" onclick="window.print()">Print</button>
        </div>
        <div class="report-main-content">
          ${inner}
        </div>
        <div class="report-print-bar report-print-bar--bottom">
          <button type="button" onclick="window.print()">Print</button>
        </div>
    `
    : inner;
  return `
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <style>
          ${PRINT_BASE_STYLES}
          ${includePrintControls ? PRINT_PREVIEW_CHROME_STYLES : ''}
          ${extraStyles || ''}
        </style>
      </head>
      <body>
        ${bodyContent}
      </body>
    </html>
  `;
};

/**
 * Opens the report in a new tab for preview. User chooses Print / Save as PDF from the buttons
 * (top and bottom); the print dialog is not opened automatically.
 */
export const openPrintWindow = ({ title, subtitle = '', bodyHtml = '', extraStyles = '' }) => {
  const html = buildReportHtml({
    title,
    subtitle,
    bodyHtml,
    extraStyles,
    includePrintControls: true,
  });
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const blobUrl = URL.createObjectURL(blob);
  const win = window.open(blobUrl, '_blank');
  if (!win) {
    URL.revokeObjectURL(blobUrl);
    return;
  }
  const cleanup = () => {
    try {
      URL.revokeObjectURL(blobUrl);
    } catch (_) {}
  };
  win.addEventListener('beforeunload', cleanup, { once: true });
};

/** Open report in a new tab (blank document); optional print controls like openPrintWindow. */
export const openReportInNewTab = ({
  title,
  subtitle = '',
  bodyHtml = '',
  extraStyles = '',
  includePrintControls = true,
}) => {
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(buildReportHtml({
    title,
    subtitle,
    bodyHtml,
    extraStyles,
    includePrintControls,
  }));
  win.document.close();
};

/** Build full HTML string for report (e.g. iframe preview or programmatic open). */
export const getReportFullHtml = ({
  title,
  subtitle = '',
  bodyHtml = '',
  extraStyles = '',
  includePrintControls = false,
}) => buildReportHtml({ title, subtitle, bodyHtml, extraStyles, includePrintControls });

export const printTableElement = ({ title, subtitle = '', element, extraStyles = '' }) => {
  if (!element) return;
  openPrintWindow({
    title,
    subtitle,
    bodyHtml: element.innerHTML,
    extraStyles,
  });
};

/** @param {number[] | null | undefined} amountColumnIndices - 0-based column indexes to right-align (amounts / counts). */
export const tableHtmlFromRows = ({ headers, rows, emptyMessage = 'No records found.', amountColumnIndices = null }) => {
  const amountSet = Array.isArray(amountColumnIndices) ? new Set(amountColumnIndices) : null;
  const cellClass = (i) => (amountSet && amountSet.has(i) ? ' class="amount-cell"' : '');
  const head = `<tr>${headers.map((h, i) => `<th${cellClass(i)}>${escapeHtml(h)}</th>`).join('')}</tr>`;
  const body = rows.length > 0
    ? rows.map((r) => `<tr>${r.map((c, i) => `<td${cellClass(i)}>${escapeHtml(c)}</td>`).join('')}</tr>`).join('')
    : `<tr><td colspan="${headers.length}" style="text-align:center;color:#6b7280">${escapeHtml(emptyMessage)}</td></tr>`;
  return `<table><thead>${head}</thead><tbody>${body}</tbody></table>`;
};
